import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { firstValueFrom } from 'rxjs';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { PssConfirmModalComponent } from '../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { ApiSystemSettingsService } from '../../../../core/services/api/system-settings/api-system-settings.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { ModalAdminActivityHistoryComponent } from '../../modals/system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../core/models/system-settings/admin-activity-log.model';
import {
  PUBLIC_GUIDE_BLOCK_TYPES,
  PUBLIC_GUIDE_KIND_OPTIONS,
  PUBLIC_GUIDE_MAX_IMAGE_BYTES,
  PublicGuideBlock,
  PublicGuideBlockType,
  PublicGuideDetail,
  PublicGuideKind,
  PublicGuideListItem,
  blockPreviewSrc,
  coverPreviewSrc,
  createEmptyPublicGuide,
  createEmptyPublicGuideBlock,
  mapPublicGuideDetail,
  mapPublicGuideListItem,
  slugifyTitle,
  toPublicGuideSavePayload,
} from '../../../../core/models/system-settings/public-guide.model';
import {
  ManualContact,
  createEmptyManualContact,
  mapManualContact,
} from '../../../../core/models/system-settings/manual-contact.model';

@Component({
  selector: 'app-public-guides',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PssLoadingComponent,
    PssConfirmModalComponent,
    ModalAdminActivityHistoryComponent,
  ],
  templateUrl: './public-guides.component.html',
  styleUrl: './public-guides.component.css',
})
export class PublicGuidesComponent implements OnInit {
  private readonly api = inject(ApiSystemSettingsService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  readonly blockTypeOptions = PUBLIC_GUIDE_BLOCK_TYPES;
  readonly kindOptions = PUBLIC_GUIDE_KIND_OPTIONS;
  readonly activityEntityTypes = AdminActivityEntityTypes;
  readonly maxImageBytes = PUBLIC_GUIDE_MAX_IMAGE_BYTES;
  readonly pageSizeOptions = [10, 20, 50, 100];

  items = signal<PublicGuideListItem[]>([]);
  totalCount = signal(0);
  page = signal(1);
  pageSize = signal(20);
  isLoading = signal(false);
  isSaving = signal(false);
  lastFetchedTime = signal<Date | null>(null);
  isDeleteConfirmOpen = signal(false);
  deleteTarget = signal<PublicGuideListItem | null>(null);

  searchText = '';
  filterPublished: '' | 'true' | 'false' = '';
  filterActive: '' | 'true' | 'false' = 'true';
  filterKind: '' | PublicGuideKind = '';

  viewMode = signal<'list' | 'edit' | 'contact'>('list');
  draft = signal<PublicGuideDetail>(createEmptyPublicGuide());
  contact = signal<ManualContact>(createEmptyManualContact());
  showActivityHistory = false;
  showImageModal = false;
  selectedImageUrl = '';

  trackById = (_: number, item: PublicGuideListItem) => item.id;
  trackByBlock = (_: number, b: PublicGuideBlock) => b.uiKey;

  ngOnInit(): void {
    this.reload();
  }

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get canDelete(): boolean {
    return this.permissionService.canDelete(this.router.url);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalCount() / this.pageSize()));
  }

  get startItemIndex(): number {
    if (this.totalCount() === 0) return 0;
    return (this.page() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.page() * this.pageSize(), this.totalCount());
  }

  get pagesArray(): number[] {
    const total = this.totalPages;
    const current = this.page();
    const windowSize = 5;
    let start = Math.max(1, current - Math.floor(windowSize / 2));
    const end = Math.min(total, start + windowSize - 1);
    start = Math.max(1, end - windowSize + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  get canShowActivityHistory(): boolean {
    return (this.draft()?.id ?? 0) > 0;
  }

  coverSrc(guide: PublicGuideDetail): string {
    return coverPreviewSrc(guide);
  }

  blockSrc(block: PublicGuideBlock): string {
    return blockPreviewSrc(block);
  }

  reload(): void {
    this.isLoading.set(true);
    this.api
      .getPublicGuidesForTable({
        search: this.searchText,
        isPublished:
          this.filterPublished === '' ? null : this.filterPublished === 'true',
        isActive: this.filterActive === '' ? null : this.filterActive === 'true',
        guideKind: this.filterKind || null,
        page: this.page(),
        pageSize: this.pageSize(),
      })
      .subscribe({
        next: (res) => {
          const rows = res?.items ?? res?.Items ?? [];
          this.items.set(rows.map(mapPublicGuideListItem));
          this.totalCount.set(Number(res?.total ?? res?.Total ?? 0));
          this.lastFetchedTime.set(new Date());
          this.isLoading.set(false);
        },
        error: () => {
          this.toast.error('โหลดคู่มือสาธารณะล้มเหลว');
          this.isLoading.set(false);
        },
      });
  }

  onSearch(): void {
    this.page.set(1);
    this.reload();
  }

  onPageChange(next: number): void {
    if (next < 1 || next > this.totalPages) return;
    this.page.set(next);
    this.reload();
  }

  onPageSizeChange(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value);
    if (!Number.isFinite(value) || value <= 0) return;
    this.pageSize.set(value);
    this.page.set(1);
    this.reload();
  }

  openCreate(): void {
    if (!this.canCreate) return;
    this.draft.set(createEmptyPublicGuide());
    this.viewMode.set('edit');
  }

  openEdit(item: PublicGuideListItem): void {
    if (!this.canUpdate && !this.canCreate) return;
    this.isLoading.set(true);
    this.api.getPublicGuideById(item.id).subscribe({
      next: (res) => {
        this.draft.set(mapPublicGuideDetail(res));
        this.viewMode.set('edit');
        this.isLoading.set(false);
      },
      error: () => {
        this.toast.error('โหลดรายละเอียดคู่มือล้มเหลว');
        this.isLoading.set(false);
      },
    });
  }

  backToList(): void {
    this.viewMode.set('list');
    this.draft.set(createEmptyPublicGuide());
  }

  openContact(): void {
    this.isLoading.set(true);
    this.api.getManualContact().subscribe({
      next: (res) => {
        this.contact.set(mapManualContact(res));
        this.viewMode.set('contact');
        this.isLoading.set(false);
      },
      error: () => {
        this.toast.error('โหลดข้อมูลติดต่อล้มเหลว');
        this.isLoading.set(false);
      },
    });
  }

  patchContact(partial: Partial<ManualContact>): void {
    this.contact.update((c) => ({ ...c, ...partial }));
  }

  async onSaveContact(): Promise<void> {
    if (!this.canUpdate && !this.canCreate) return;
    const auth = this.resolveAuth();
    if (!auth) return;
    const c = this.contact();
    if (!c.companyNameTh?.trim()) {
      this.toast.error('กรุณาระบุชื่อบริษัท');
      return;
    }
    this.isSaving.set(true);
    try {
      const res = await firstValueFrom(
        this.api.saveManualContact({
          userId: auth.userId,
          menuId: auth.menuId,
          id: c.id,
          companyNameTh: c.companyNameTh.trim(),
          addressTh: c.addressTh?.trim() || null,
          phone: c.phone?.trim() || null,
          phoneSecondary: c.phoneSecondary?.trim() || null,
          email: c.email?.trim() || null,
          noteTh: c.noteTh?.trim() || null,
          isActive: c.isActive,
        }),
      );
      const ok = res?.isSuccess ?? res?.IsSuccess ?? true;
      if (ok === false) {
        this.toast.error(res?.message ?? res?.Message ?? 'บันทึกไม่สำเร็จ');
        return;
      }
      this.toast.success(res?.message ?? res?.Message ?? 'บันทึกเรียบร้อย');
      this.viewMode.set('list');
    } catch {
      this.toast.error('บันทึกข้อมูลติดต่อล้มเหลว');
    } finally {
      this.isSaving.set(false);
    }
  }

  kindLabel(kind: string): string {
    return kind === 'ev' ? 'EV' : 'Parking';
  }

  patchDraft(partial: Partial<PublicGuideDetail>): void {
    this.draft.update((d) => ({ ...d, ...partial }));
  }

  onTitleChange(title: string): void {
    const d = this.draft();
    const next: Partial<PublicGuideDetail> = { titleTh: title };
    if (!d.id && !d.slug?.trim()) {
      next.slug = slugifyTitle(title);
    }
    this.patchDraft(next);
  }

  addBlock(type: PublicGuideBlockType = 'text'): void {
    if (!this.canUpdate && !this.canCreate) return;
    this.draft.update((d) => {
      const nextSort = d.blocks.length
        ? Math.max(...d.blocks.map((b) => b.sortOrder)) + 10
        : 10;
      const block = createEmptyPublicGuideBlock(nextSort);
      block.blockType = type;
      return { ...d, blocks: [...d.blocks, block] };
    });
  }

  removeBlock(index: number): void {
    if (!this.canUpdate && !this.canCreate) return;
    this.draft.update((d) => ({
      ...d,
      blocks: d.blocks.filter((_, i) => i !== index),
    }));
  }

  moveBlock(index: number, direction: -1 | 1): void {
    if (!this.canUpdate && !this.canCreate) return;
    this.draft.update((d) => {
      const blocks = [...d.blocks];
      const target = index + direction;
      if (target < 0 || target >= blocks.length) return d;
      [blocks[index], blocks[target]] = [blocks[target], blocks[index]];
      blocks.forEach((b, i) => (b.sortOrder = (i + 1) * 10));
      return { ...d, blocks };
    });
  }

  /** mutate แถวเดิมเพื่อไม่ทำลาย focus ตอนพิมพ์ */
  patchBlock(index: number, partial: Partial<PublicGuideBlock>): void {
    this.draft.update((d) => {
      const block = d.blocks[index];
      if (block) Object.assign(block, partial);
      return { ...d, blocks: d.blocks };
    });
  }

  onCoverSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (!this.canEditDraft()) {
      input.value = '';
      return;
    }
    this.readImageFile(file, (base64, name) => {
      this.patchDraft({
        coverPendingBase64: base64,
        coverPendingFileName: name,
      });
      this.toast.success('แนบรูปปกแล้ว — กดบันทึกเพื่ออัปโหลด');
    });
    input.value = '';
  }

  clearCover(): void {
    this.patchDraft({
      coverPendingBase64: null,
      coverPendingFileName: null,
      coverImage: '',
      coverImagePath: '',
      coverImageExt: '',
    });
  }

  onBlockImageSelected(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (!this.canEditDraft()) {
      input.value = '';
      return;
    }
    this.readImageFile(file, (base64, name) => {
      this.patchBlock(index, {
        blockType: 'image',
        pendingBase64: base64,
        pendingFileName: name,
      });
      this.toast.success('แนบรูปแล้ว — กดบันทึกเพื่ออัปโหลด');
    });
    input.value = '';
  }

  clearBlockImage(index: number): void {
    this.patchBlock(index, {
      pendingBase64: null,
      pendingFileName: null,
      image: '',
      imagePath: '',
      imageExt: '',
    });
  }

  async onSave(): Promise<void> {
    if (!this.canEditDraft()) return;
    const auth = this.resolveAuth();
    if (!auth) return;

    const guide = this.draft();
    if (!guide.titleTh?.trim()) {
      this.toast.error('กรุณาระบุหัวข้อ');
      return;
    }
    if (!guide.slug?.trim()) {
      this.toast.error('กรุณาระบุ slug');
      return;
    }
    if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(guide.slug.trim().toLowerCase())) {
      this.toast.error('slug ต้องเป็น a-z 0-9 และขีดกลางเท่านั้น');
      return;
    }

    this.isSaving.set(true);
    try {
      const withUploads = await this.uploadPending(guide, auth);
      this.draft.set(withUploads);

      const res = await firstValueFrom(
        this.api.savePublicGuide(toPublicGuideSavePayload(withUploads, auth.userId, auth.menuId)),
      );
      const ok = res?.isSuccess ?? res?.IsSuccess ?? res?.success ?? true;
      if (ok === false) {
        this.toast.error(res?.message ?? res?.Message ?? 'บันทึกไม่สำเร็จ');
        return;
      }
      this.toast.success(res?.message ?? res?.Message ?? 'บันทึกเรียบร้อย');
      this.viewMode.set('list');
      this.reload();
    } catch {
      this.toast.error('บันทึกคู่มือล้มเหลว');
    } finally {
      this.isSaving.set(false);
    }
  }

  askDelete(item: PublicGuideListItem): void {
    if (!this.canDelete) return;
    this.deleteTarget.set(item);
    this.isDeleteConfirmOpen.set(true);
  }

  closeDeleteConfirm(): void {
    this.isDeleteConfirmOpen.set(false);
    this.deleteTarget.set(null);
  }

  async confirmDelete(): Promise<void> {
    const item = this.deleteTarget();
    if (!item) return;
    await this.onDelete(item);
  }

  async onDelete(item: PublicGuideListItem): Promise<void> {
    if (!this.canDelete) return;
    const auth = this.resolveAuth();
    if (!auth) return;

    this.isSaving.set(true);
    try {
      const res = await firstValueFrom(
        this.api.deletePublicGuide({ userId: auth.userId, menuId: auth.menuId, id: item.id }),
      );
      const ok = res?.isSuccess ?? res?.IsSuccess ?? true;
      if (ok === false) {
        this.toast.error(res?.message ?? res?.Message ?? 'ลบไม่สำเร็จ');
        return;
      }
      this.toast.success(res?.message ?? res?.Message ?? 'ลบเรียบร้อย');
      this.closeDeleteConfirm();
      this.reload();
    } catch {
      this.toast.error('ลบคู่มือล้มเหลว');
    } finally {
      this.isSaving.set(false);
    }
  }

  openImageModal(url: string): void {
    if (!url) return;
    this.selectedImageUrl = url;
    this.showImageModal = true;
  }

  closeImageModal(): void {
    this.showImageModal = false;
    this.selectedImageUrl = '';
  }

  openHistory(): void {
    this.showActivityHistory = true;
  }

  private canEditDraft(): boolean {
    const id = this.draft().id;
    return id > 0 ? this.canUpdate : this.canCreate;
  }

  private async uploadPending(
    guide: PublicGuideDetail,
    auth: { userId: number; menuId: number },
  ): Promise<PublicGuideDetail> {
    let next = { ...guide, blocks: guide.blocks.map((b) => ({ ...b })) };

    if (next.coverPendingBase64) {
      const uploaded = await this.uploadOne(next.coverPendingBase64, next.coverPendingFileName, auth);
      next = {
        ...next,
        coverImagePath: uploaded.path,
        coverImageExt: uploaded.ext,
        coverImage: uploaded.url,
        coverPendingBase64: null,
        coverPendingFileName: null,
      };
    }

    for (let i = 0; i < next.blocks.length; i++) {
      const block = next.blocks[i];
      if (block.blockType !== 'image' || !block.pendingBase64) continue;
      const uploaded = await this.uploadOne(block.pendingBase64, block.pendingFileName, auth);
      next.blocks[i] = {
        ...block,
        imagePath: uploaded.path,
        imageExt: uploaded.ext,
        image: uploaded.url,
        pendingBase64: null,
        pendingFileName: null,
      };
    }

    return next;
  }

  private async uploadOne(
    base64: string,
    fileName: string | null,
    auth: { userId: number; menuId: number },
  ): Promise<{ path: string; ext: string; url: string }> {
    const res = await firstValueFrom(
      this.api.uploadPublicGuideImage({
        userId: auth.userId,
        menuId: auth.menuId,
        base64,
        fileName: fileName || undefined,
      }),
    );
    const ok = res?.isSuccess ?? res?.IsSuccess ?? false;
    const path = res?.imagePath ?? res?.ImagePath ?? '';
    const ext = res?.imageExt ?? res?.ImageExt ?? '';
    const url = res?.imageUrl ?? res?.ImageUrl ?? '';
    if (!ok || !path || !ext) {
      throw new Error(res?.message ?? res?.Message ?? 'อัปโหลดรูปไม่สำเร็จ');
    }
    return { path, ext, url };
  }

  private readImageFile(file: File, onOk: (base64: string, name: string) => void): void {
    if (!file.type.startsWith('image/')) {
      this.toast.error('กรุณาเลือกไฟล์รูปภาพ');
      return;
    }
    if (file.size > this.maxImageBytes) {
      this.toast.error('ไฟล์ใหญ่เกิน 10MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onOk(String(reader.result || ''), file.name);
    reader.readAsDataURL(file);
  }

  private resolveAuth(): { userId: number; menuId: number } | null {
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!menuId) {
      this.toast.error(
        'ไม่พบสิทธิ์เมนูนี้ — ตรวจว่า path /system-settings/public-guides (path_id 127) ถูกผูก role แล้ว',
      );
      return null;
    }
    return { userId: 0, menuId };
  }
}
